import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar, faCheck, faTimes, faArrowRight, faArrowLeft, faSmile, faMeh, faFrown } from "@fortawesome/free-solid-svg-icons";
import API_BASE_URL from "../config/api";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translate(-50%, -45%) scale(0.95); }
  to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

// Styled Components
const Overlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(5px);
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease-out;
`;

const PopupContainer = styled.div`
  position: fixed;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);
  z-index: 10001;
  animation: ${slideUp} 0.4s ease-out;
  display: flex;
  flex-direction: column;
  padding: 0;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background-color: #ccc; border-radius: 4px; }
`;

const Header = styled.div`
  padding: 24px;
  background: #f8fbfd;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Title = styled.h2`
  font-family: "Merriweather", serif;
  color: #004E61;
  font-size: 20px;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #a0aec0;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  &:hover { color: #4a5568; }
`;

const ProgressBar = styled.div`
  height: 4px;
  background: #edf2f7;
  width: 100%;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #1fa2ff, #12d8fa);
  width: ${props => props.width}%;
  transition: width 0.3s ease;
`;

const Content = styled.div`
  padding: 30px 24px;
  flex: 1;
`;

const QuestionGroup = styled.div`
  margin-bottom: 24px;
`;

const QuestionLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 12px;
  font-size: 16px;
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 10;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  ${props => props.variant === 'primary' ? css`
    background: #004E61;
    color: white;
    border: none;
    &:hover { background: #003845; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  ` : css`
    background: white;
    border: 1px solid #cbd5e0;
    color: #4a5568;
    &:hover { background: #f7fafc; }
  `}
`;

// Input Components
const StarRating = ({ rating, setRating, max = 5 }) => {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      {[...Array(max)].map((_, i) => {
        const value = i + 1;
        return (
          <button
            key={value}
            onClick={() => setRating(value)}
            onMouseEnter={() => setHover(value)}
            onMouseLeave={() => setHover(0)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '28px', color: value <= (hover || rating) ? '#FFB400' : '#CBD5E0', transition: 'color 0.2s' }}
            type="button"
          >
            <FontAwesomeIcon icon={faStar} />
          </button>
        );
      })}
    </div>
  );
};

const TagButton = styled.button`
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid ${props => props.selected ? '#004E61' : '#E2E8F0'};
  background: ${props => props.selected ? '#E6FFFA' : 'white'};
  color: ${props => props.selected ? '#004E61' : '#4A5568'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #004E61; }
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  min-height: 100px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
  &:focus { outline: none; border-color: #004E61; box-shadow: 0 0 0 2px rgba(0,78,97,0.1); }
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  margin-top: 5px;
  &:focus { outline: none; border-color: #004E61; box-shadow: 0 0 0 2px rgba(0,78,97,0.1); }
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 8px 0;
  color: #4a5568;
  font-size: 15px;
  
  input {
    accent-color: #004E61;
    width: 18px;
    height: 18px;
  }
`;

const NPSContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 4px;
  
  @media (max-width: 500px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const NPSButton = styled.button`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${props => props.selected ? '#004E61' : '#E2E8F0'};
  background: ${props => props.selected ? '#004E61' : 'white'};
  color: ${props => props.selected ? 'white' : '#4A5568'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { border-color: #004E61; transform: scale(1.1); }
`;

const FeedbackPopup = ({ isOpen, onClose, triggerType = "manual_button", email = "", bookingId = "" }) => {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    uxRating: 0,
    speedRating: 0,
    aiSatisfaction: "",
    aiHelpfulFeatures: [],
    aiPersonalized: "",
    likedFeatures: [],
    improvementArea: "",
    bookingConfidence: "",
    bookingDropoffReason: "",
    npsScore: null,
    npsScore: null,
    additionalComments: "",
    name: "",
    phone: ""
  });

  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setIsSuccess(false);
      setFormData({
        uxRating: 0,
        speedRating: 0,
        aiSatisfaction: "",
        aiHelpfulFeatures: [],
        aiPersonalized: "",
        likedFeatures: [],
        improvementArea: "",
        bookingConfidence: "",
        bookingDropoffReason: "",
        npsScore: null,
        npsScore: null,
        additionalComments: "",
        name: "",
        phone: ""
      });
    }
  }, [isOpen]);

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(item => item !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    // Generate unique submission ID
    const submissionId = `feedback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    try {
      const response = await fetch(`${API_BASE_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          triggerType,
          email,
          bookingId,
          sessionId: submissionId
        }),
      });

      if (response.ok || response.status === 409) {
        setIsSuccess(true);
        setTimeout(() => onClose(), 2500);
      } else {
        console.error("Failed to submit feedback");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSteps = 6;

  const renderStep = () => {
    switch (step) {
      case 1: // UX
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>1. How easy was it to use the Trippro website?</QuestionLabel>
              <StarRating rating={formData.uxRating} setRating={(v) => updateField('uxRating', v)} />
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>2. How would you rate the speed and performance?</QuestionLabel>
              <StarRating rating={formData.speedRating} setRating={(v) => updateField('speedRating', v)} />
            </QuestionGroup>
          </>
        );
      case 2: // AI
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>3. How satisfied are you with the AI trip customization?</QuestionLabel>
              <TagsContainer>
                {["Very Satisfied", "Satisfied", "Neutral", "Unsatisfied", "Very Unsatisfied"].map(opt => (
                  <TagButton
                    key={opt}
                    selected={formData.aiSatisfaction === opt}
                    onClick={() => updateField('aiSatisfaction', opt)}
                  >
                    {opt}
                  </TagButton>
                ))}
              </TagsContainer>
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>4. What part of the AI customization was most helpful?</QuestionLabel>
              <TagsContainer>
                {["Personalized itinerary", "Hotel recommendations", "Activity suggestions", "Budget optimization", "Destination curation"].map(opt => (
                  <TagButton
                    key={opt}
                    selected={formData.aiHelpfulFeatures.includes(opt)}
                    onClick={() => toggleArrayField('aiHelpfulFeatures', opt)}
                  >
                    {opt}
                  </TagButton>
                ))}
              </TagsContainer>
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>5. Did the itinerary feel personalized?</QuestionLabel>
              <TagsContainer>
                {["Yes", "Somewhat", "No"].map(opt => (
                  <TagButton
                    key={opt}
                    selected={formData.aiPersonalized === opt}
                    onClick={() => updateField('aiPersonalized', opt)}
                  >
                    {opt}
                  </TagButton>
                ))}
              </TagsContainer>
            </QuestionGroup>
          </>
        );
      case 3: // Features
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>6. Which feature did you like the most?</QuestionLabel>
              <TagsContainer>
                {["AI Package Customization", "Price Breakup Transparency", "Ease of Booking", "Package Pricing"].map(opt => (
                  <TagButton
                    key={opt}
                    selected={formData.likedFeatures.includes(opt)}
                    onClick={() => toggleArrayField('likedFeatures', opt)}
                  >
                    {opt}
                  </TagButton>
                ))}
              </TagsContainer>
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>7. What needs improvement?</QuestionLabel>
              <TextArea
                placeholder="Share your thoughts..."
                value={formData.improvementArea}
                onChange={(e) => updateField('improvementArea', e.target.value)}
              />
            </QuestionGroup>
          </>
        );
      case 4: // Trust
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>8. Did you feel confident enough to book with us?</QuestionLabel>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {["Yes, definitely", "Maybe, needs improvement", "Not right now"].map(opt => (
                  <RadioLabel key={opt}>
                    <input
                      type="radio"
                      name="bookingConfidence"
                      checked={formData.bookingConfidence === opt}
                      onChange={() => updateField('bookingConfidence', opt)}
                    />
                    {opt}
                  </RadioLabel>
                ))}
              </div>
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>9. If you didn't book, what stopped you?</QuestionLabel>
              <TextArea
                placeholder="Price, trust, missing info..."
                value={formData.bookingDropoffReason}
                onChange={(e) => updateField('bookingDropoffReason', e.target.value)}
              />
            </QuestionGroup>
          </>
        );
      case 5: // Sentiment (NPS)
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>10. Would you recommend TripPro to friends? (0-10)</QuestionLabel>
              <NPSContainer>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                  <NPSButton
                    key={num}
                    selected={formData.npsScore === num}
                    onClick={() => updateField('npsScore', num)}
                  >
                    {num}
                  </NPSButton>
                ))}
              </NPSContainer>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '12px', color: '#718096' }}>
                <span>Not Likely</span>
                <span>Very Likely</span>
              </div>
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>11. Any final thoughts?</QuestionLabel>
              <TextArea
                placeholder="We'd love to hear them..."
                value={formData.additionalComments}
                onChange={(e) => updateField('additionalComments', e.target.value)}
              />
            </QuestionGroup>
          </>
        );
      case 6: // Contact Details
        return (
          <>
            <QuestionGroup>
              <QuestionLabel>12. Your Name <span style={{ color: "red" }}>*</span></QuestionLabel>
              <Input
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </QuestionGroup>
            <QuestionGroup>
              <QuestionLabel>13. Phone Number <span style={{ color: "red" }}>*</span></QuestionLabel>
              <Input
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => updateField('phone', e.target.value)}
              />
            </QuestionGroup>
          </>
        );
      default: return null;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Overlay onClick={onClose} />
      <PopupContainer>
        {isSuccess ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎉</div>
            <h2 style={{ color: '#004E61', marginBottom: '10px' }}>Thank You!</h2>
            <p style={{ color: '#4a5568' }}>Your feedback helps us build a better travel experience.</p>
          </div>
        ) : (
          <>
            <Header>
              <Title>Help us improve</Title>
              <CloseButton onClick={onClose}><FontAwesomeIcon icon={faTimes} /></CloseButton>
            </Header>
            <ProgressBar>
              <ProgressFill width={(step / totalSteps) * 100} />
            </ProgressBar>

            <Content>{renderStep()}</Content>

            <Footer>
              {step > 1 ? (
                <Button onClick={() => setStep(s => s - 1)}>
                  <FontAwesomeIcon icon={faArrowLeft} /> Back
                </Button>
              ) : (
                <div /> // Spacer
              )}

              {step < totalSteps ? (
                <Button variant="primary" onClick={() => setStep(s => s + 1)}>
                  Next <FontAwesomeIcon icon={faArrowRight} />
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !formData.name.trim() || !formData.phone.trim()}
                >
                  {isSubmitting ? "Sending..." : "Submit Feedback"}
                </Button>
              )}
            </Footer>
          </>
        )}
      </PopupContainer>
    </>
  );
};

export default FeedbackPopup;
